/**
 * Display formatting for money, quantities, counts and percentages. **One number style for
 * the whole product** (DECISIONS D-022): decimal point, thousands grouped with a
 * non-breaking space, and a true minus sign (U+2212). `formatQty('1234.5', 'kg')` →
 * `1 234.5 kg`; `formatPercent(1250)` → `12.5%`; money (see `formatMoney`) → `R 12 480.00`,
 * `−R 99.00`. To change the style (e.g. back to the SI decimal comma), change
 * {@link NUMBER_STYLE} and nothing else. Numbers are formatted from decimal strings, never
 * through floats, and never through `Intl` (whose output varies between ICU builds).
 * @module
 */
import { type QtyInput, type RoundingMode, qty, qtyToString, roundQty } from '../quantity/index.js';

/** The product's number style (D-022). The only place separators and the minus sign live. */
export const NUMBER_STYLE = Object.freeze({
  /** Between whole and fractional digits. */
  decimal: '.',
  /** Between thousands groups: a non-breaking space, so a number never wraps. */
  group: '\u00a0',
  /** Before negative values: U+2212 MINUS SIGN, not a hyphen. */
  minus: '\u2212',
});

/** A non-breaking space: between a currency symbol or unit and its number. */
const NBSP = '\u00a0';

/**
 * Formats a plain decimal string (`"-1234.50"`) in {@link NUMBER_STYLE}. Exact: digits are
 * only regrouped, never rounded, so round first. Negative zero prints without a sign.
 * Returns the unsigned text and whether a minus is due, for callers that put a symbol
 * between the sign and the digits (money).
 */
export function formatDecimalParts(text: string): { negative: boolean; digits: string } {
  const m = /^(-)?(\d+)(?:\.(\d+))?$/.exec(text);
  if (!m) throw new RangeError(`Not a decimal number: ${JSON.stringify(text)}`);
  const [, sign, whole = '0', fraction = ''] = m;
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, NUMBER_STYLE.group);
  const negative = sign === '-' && /[1-9]/.test(whole + fraction);
  return { negative, digits: fraction ? `${grouped}${NUMBER_STYLE.decimal}${fraction}` : grouped };
}

/** {@link formatDecimalParts} with the minus sign in front: `"-1234.5"` → `−1 234.5`. */
export function formatDecimalText(text: string): string {
  const { negative, digits } = formatDecimalParts(text);
  return `${negative ? NUMBER_STYLE.minus : ''}${digits}`;
}

/** Drops trailing fractional zeros (and a bare decimal point): `"1.500"` → `"1.5"`. */
function trimZeros(text: string): string {
  return text.includes('.') ? text.replace(/\.?0+$/, '') : text;
}

/** Options for {@link formatQty}. */
export interface FormatQtyOptions {
  /** Most decimals to show (default 3); trailing zeros are always trimmed. */
  maxDecimals?: number;
  /** Rounding when the value has more decimals (default `half_up`). */
  rounding?: RoundingMode;
}

/** A quantity with no unit: `1.5`, `1 234.5`, `−0.25`. Rounded exactly, zeros trimmed. */
export function formatNumber(value: QtyInput, options: FormatQtyOptions = {}): string {
  const maxDecimals = options.maxDecimals ?? 3;
  const rounded = roundQty(qty(value), maxDecimals, options.rounding ?? 'half_up');
  return formatDecimalText(trimZeros(qtyToString(rounded)));
}

/**
 * A quantity with its unit: `1.5 kg`, `12 ea`, `1 234.5 g`. Trailing zeros trimmed, at
 * most 3 decimals by default, a non-breaking space before the unit so it never wraps.
 */
export function formatQty(value: QtyInput, uom: string, options: FormatQtyOptions = {}): string {
  return `${formatNumber(value, options)}${NBSP}${uom}`;
}

/** An integer count with grouping: 1445 → `1 445` (non-breaking space). */
export function formatCount(n: number): string {
  if (!Number.isSafeInteger(n)) throw new RangeError(`Count must be an integer, got ${n}`);
  return formatDecimalText(String(n));
}

/**
 * Basis points as a percentage: 1500 → `15%`, 1250 → `12.5%`, 5 → `0.05%`.
 * Basis points are integers, so the result is exact.
 */
export function formatPercent(bp: number): string {
  if (!Number.isSafeInteger(bp)) throw new RangeError(`Basis points must be an integer, got ${bp}`);
  const abs = Math.abs(bp);
  const text = `${bp < 0 ? '-' : ''}${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
  return `${formatDecimalText(trimZeros(text))}%`;
}
